import type { ReactNode, SVGProps } from "react";

export type IconProps = Omit<SVGProps<SVGSVGElement>, "children"> & {
  readonly size?: number;
};

const makeIcon = (paths: ReactNode, vb = 24) =>
  function IconGlyph({ size = 16, ...rest }: IconProps) {
    return (
      <svg
        fill="none"
        height={size}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.6}
        viewBox={`0 0 ${vb} ${vb}`}
        width={size}
        {...rest}
      >
        {paths}
      </svg>
    );
  };

// Minimal outline icon set (1.6 stroke, currentColor) ported from the design.
export const Icon = {
  Archive: makeIcon(
    <>
      <rect height="4" rx="1" width="18" x="3" y="4" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
      <path d="M10 12h4" />
    </>,
  ),
  ArrowDown: makeIcon(<path d="M12 5v14M5 12l7 7 7-7" />),
  ArrowLeft: makeIcon(<path d="M19 12H5M11 5l-7 7 7 7" />),
  ArrowRight: makeIcon(<path d="M5 12h14M13 5l7 7-7 7" />),
  ArrowUp: makeIcon(<path d="M12 19V5M5 12l7-7 7 7" />),
  Bell: makeIcon(
    <>
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8z" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </>,
  ),
  Branch: makeIcon(
    <>
      <circle cx="6" cy="5" r="2" />
      <circle cx="6" cy="19" r="2" />
      <circle cx="18" cy="12" r="2" />
      <path d="M6 7v10" />
      <path d="M6 12h7a4 4 0 0 0 4-4V7" />
    </>,
  ),
  Chat: makeIcon(
    <path d="M21 12a8 8 0 0 1-11.6 7.1L3 21l1.9-6.4A8 8 0 1 1 21 12z" />,
  ),
  Check: makeIcon(<path d="M5 12l5 5 9-11" />),
  ChevDown: makeIcon(<path d="M6 9l6 6 6-6" />),
  Chevron: makeIcon(<path d="M9 6l6 6-6 6" />),
  Clock: makeIcon(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>,
  ),
  Cmd: makeIcon(
    <path d="M9 3a3 3 0 0 1 3 3v12a3 3 0 1 1-3-3h12a3 3 0 1 1-3 3V6a3 3 0 1 1 3 3H9a3 3 0 1 1 3-3z" />,
  ),
  Cog: makeIcon(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>,
  ),
  Comment: makeIcon(
    <path d="M21 12a8 8 0 0 1-11.6 7.1L3 21l1.9-6.4A8 8 0 1 1 21 12z" />,
  ),
  Copy: makeIcon(
    <>
      <rect height="12" rx="2" width="12" x="9" y="9" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </>,
  ),
  Cpu: makeIcon(
    <>
      <rect height="12" rx="2" width="12" x="6" y="6" />
      <rect height="6" rx="1" width="6" x="9" y="9" />
      <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
    </>,
  ),
  Doc: makeIcon(
    <>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
    </>,
  ),
  Dot3: makeIcon(
    <>
      <circle cx="5" cy="12" fill="currentColor" r="1.5" />
      <circle cx="12" cy="12" fill="currentColor" r="1.5" />
      <circle cx="19" cy="12" fill="currentColor" r="1.5" />
    </>,
  ),
  External: makeIcon(
    <>
      <path d="M14 5h5v5" />
      <path d="M19 5l-9 9" />
      <path d="M19 14v5H5V5h5" />
    </>,
  ),
  Filter: makeIcon(<path d="M3 5h18l-7 9v6l-4-2v-4z" />),
  Folder: makeIcon(
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
  ),
  GitDiff: makeIcon(
    <>
      <circle cx="6" cy="6" r="2" />
      <circle cx="18" cy="18" r="2" />
      <path d="M6 8v3a3 3 0 0 0 3 3h6" />
      <path d="M18 16v-3a3 3 0 0 0-3-3H9" />
    </>,
  ),
  Github: makeIcon(
    <path d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.46-1.11-1.46-.91-.62.07-.61.07-.61 1 .07 1.53 1.04 1.53 1.04.9 1.53 2.34 1.09 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.99 1.03-2.69-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.03a9.5 9.5 0 0 1 5 0c1.91-1.3 2.75-1.03 2.75-1.03.55 1.38.2 2.4.1 2.65.64.7 1.03 1.6 1.03 2.69 0 3.84-2.34 4.69-4.57 4.93.36.31.67.92.67 1.86v2.76c0 .27.18.58.69.48A10 10 0 0 0 12 2z" />,
  ),
  Gitlab: makeIcon(<path d="M12 22 5 14l-2-7 4 0 5 8 5-8 4 0-2 7z" />),
  Globe: makeIcon(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </>,
  ),
  Help: makeIcon(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 4" />
      <circle cx="12" cy="17" fill="currentColor" r=".7" />
    </>,
  ),
  Home: makeIcon(
    <>
      <path d="M3 11l9-7 9 7" />
      <path d="M5 10v10h14V10" />
    </>,
  ),
  Inbox: makeIcon(
    <>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.5 5h13l3.5 7v7a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2v-7z" />
    </>,
  ),
  Kanban: makeIcon(
    <>
      <rect height="16" rx="1" width="5" x="3" y="4" />
      <rect height="10" rx="1" width="5" x="10" y="4" />
      <rect height="13" rx="1" width="4" x="17" y="4" />
    </>,
  ),
  List: makeIcon(<path d="M3 6h18M3 12h18M3 18h18" />),
  Paperclip: makeIcon(
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />,
  ),
  Pencil: makeIcon(
    <path d="M4 20l1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19l-4 1zM13.5 7.5l3 3" />,
  ),
  Play: makeIcon(<path d="M7 5l12 7-12 7z" />),
  Plus: makeIcon(<path d="M12 5v14M5 12h14" />),
  Power: makeIcon(
    <>
      <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
      <path d="M12 2v10" />
    </>,
  ),
  PullRequest: makeIcon(
    <>
      <circle cx="6" cy="6" r="2" />
      <circle cx="6" cy="18" r="2" />
      <circle cx="18" cy="18" r="2" />
      <path d="M6 8v8" />
      <path d="M18 16V9a3 3 0 0 0-3-3h-2" />
      <path d="M14 4l-2 2 2 2" />
    </>,
  ),
  Refresh: makeIcon(
    <>
      <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
      <path d="M3 21v-5h5" />
    </>,
  ),
  Repo: makeIcon(
    <>
      <path d="M4 4h13a2 2 0 0 1 2 2v14H6a2 2 0 0 1-2-2z" />
      <path d="M4 16h15" />
      <path d="M8 4v12" />
    </>,
  ),
  Search: makeIcon(
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4-4" />
    </>,
  ),
  Send: makeIcon(
    <>
      <path d="M22 2 11 13" />
      <path d="M22 2l-7 20-4-9-9-4z" />
    </>,
  ),
  Server: makeIcon(
    <>
      <rect height="7" rx="2" width="18" x="3" y="4" />
      <rect height="7" rx="2" width="18" x="3" y="13" />
      <circle cx="7" cy="7.5" fill="currentColor" r=".5" />
      <circle cx="7" cy="16.5" fill="currentColor" r=".5" />
    </>,
  ),
  Shield: makeIcon(<path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />),
  Spark: makeIcon(
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />,
  ),
  Stop: makeIcon(<rect height="12" rx="2" width="12" x="6" y="6" />),
  Terminal: makeIcon(
    <>
      <rect height="14" rx="2" width="18" x="3" y="5" />
      <path d="M7 10l3 2-3 2" />
      <path d="M13 14h4" />
    </>,
  ),
  X: makeIcon(<path d="M6 6l12 12M6 18L18 6" />),
  Zap: makeIcon(<path d="M13 2L4 14h7l-1 8 9-12h-7z" />),
};
